import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CountdownTypes } from '../enums/countdown-types.enum';
import { CountdownTickInterface } from '../interfaces/countdown-tick.interface';

const STROKE_DASH = 904; // (radius of inner circle * pi) * 2
const STROKE_DASH_PER_SECOND = STROKE_DASH / 60;
@Component({
  selector: 'mb-countdown',
  templateUrl: './countdown.component.html',
  styleUrls: ['./countdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<boolean>();

  public countdownTypes = CountdownTypes;
  public now: Date = new Date();
  public indicatorPercent = 0;
  public strokeOffset = 0;

  @Input() days!: number;
  @Input() hours!: number;
  @Input() minutes!: number;
  @Input() seconds!: number;

  @Input() countdownTo: Date = new Date();
  @Input() countdownType: CountdownTypes = CountdownTypes.FULL;
  @Input() countdownLabel = '';
  @Input() preCountdownText = '';
  @Input() hideRedundant = false;

  @Output() finished = new EventEmitter();
  @Output() tick = new EventEmitter();

  constructor(private cdr: ChangeDetectorRef) {}
  get countDown() {
    return this.countdownTo ? Math.max(0, this.countdownTo.getTime() - this.now.getTime()) : 0;
  }

  private _tick() {
    this.now = new Date();

    if (this.countdownTo.getTime() < this.now.getTime()) {
      this.finished.emit();
    }

    const tick = this.paddedDiff(this.countDown);
    const { days, hours, minutes, seconds } = tick;

    this.days = days;
    this.hours = hours;
    this.minutes = minutes;
    this.seconds = seconds;

    this.indicatorPercent = seconds > 0 ? (seconds / 60) * 100 : 0;
    this.strokeOffset = Math.round((60 - seconds) * STROKE_DASH_PER_SECOND);
    this.tick.emit(tick);
    this.cdr.detectChanges();
  }

  private paddedDiff(msDifference: number): CountdownTickInterface {
    const days = Math.floor(msDifference / 1000 / 24 / 3600);
    const parts = new Date(msDifference).toISOString().substr(11, 8).split(':');
    const hours = parseFloat(parts[0]);
    const minutes = parseFloat(parts[1]);
    const seconds = parseFloat(parts[2]);
    return {
      days: days < 0 ? 0 : days < 10 ? 0 + days : days,
      hours: hours < 0 ? 0 : hours,
      minutes: minutes < 0 ? 0 : minutes,
      seconds: seconds < 0 ? 0 : seconds,
    };
  }

  ngOnInit() {
    timer(0, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this._tick();
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
